import { ChevronDown, ChevronRight, FileText, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { WikiManifestPage } from '../api/wiki.js';
import { cn } from '../lib/cn.js';

import { buildWikiTree, collectAncestorPaths, type WikiTreeNode } from './wiki-tree-builder.js';

interface WikiSidebarProps {
  readonly pages: readonly WikiManifestPage[];
  readonly activeSlug: string | undefined;
  readonly onSelect: (slug: string) => void;
}

export function WikiSidebar({ pages, activeSlug, onSelect }: WikiSidebarProps): JSX.Element {
  const tree = useMemo(() => buildWikiTree(pages), [pages]);
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!activeSlug) return;
    const ancestors = collectAncestorPaths(tree, activeSlug);
    if (ancestors.size === 0) return;
    setOpen((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const path of ancestors) {
        if (!next.has(path)) {
          next.add(path);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tree, activeSlug]);

  const toggle = (path: string): void => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  return (
    <nav
      aria-label="Wiki pages"
      className="scrollbar-thin sticky top-4 flex max-h-[calc(100vh-6rem)] flex-col gap-0.5 overflow-y-auto rounded-2xl border border-border-subtle bg-surface/60 p-2 backdrop-blur"
    >
      <div className="px-2 pb-1.5 pt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-text-muted">
        Pages
      </div>
      {tree.map((node) => (
        <WikiTreeNodeView
          key={node.fullPath}
          node={node}
          depth={0}
          activeSlug={activeSlug}
          open={open}
          onToggle={toggle}
          onSelect={onSelect}
        />
      ))}
    </nav>
  );
}

interface WikiTreeNodeViewProps {
  readonly node: WikiTreeNode;
  readonly depth: number;
  readonly activeSlug: string | undefined;
  readonly open: ReadonlySet<string>;
  readonly onToggle: (path: string) => void;
  readonly onSelect: (slug: string) => void;
}

function WikiTreeNodeView({
  node,
  depth,
  activeSlug,
  open,
  onToggle,
  onSelect,
}: WikiTreeNodeViewProps): JSX.Element {
  const isFolder = !node.page && node.children.length > 0;
  const isLeaf = Boolean(node.page);
  const isOpen = open.has(node.fullPath);
  const active = isLeaf && node.page?.slug === activeSlug;
  const indent = { paddingLeft: 8 + depth * 12 };

  if (isFolder) {
    return (
      <div>
        <button
          type="button"
          onClick={() => onToggle(node.fullPath)}
          style={indent}
          className="group flex w-full items-center gap-1.5 rounded-lg border border-transparent py-1.5 pr-3 text-left text-sm text-text-secondary transition-colors hover:bg-hover hover:text-text-primary"
        >
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5 flex-none text-text-muted" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 flex-none text-text-muted" />
          )}
          <span className="min-w-0 flex-1 truncate font-medium">{node.name}</span>
        </button>
        {isOpen && node.children.length > 0 && (
          <div className="flex flex-col gap-0.5">
            {node.children.map((child) => (
              <WikiTreeNodeView
                key={child.fullPath}
                node={child}
                depth={depth + 1}
                activeSlug={activeSlug}
                open={open}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (!node.page) return <></>;
  const page = node.page;

  return (
    <button
      type="button"
      onClick={() => onSelect(page.slug)}
      style={indent}
      className={cn(
        'group flex items-center gap-2 rounded-lg border py-2 pr-3 text-left text-sm transition-colors',
        active
          ? 'border-accent/40 bg-accent/15 text-text-primary'
          : 'border-transparent text-text-secondary hover:bg-hover hover:text-text-primary',
      )}
    >
      <FileText
        className={cn('h-3.5 w-3.5 flex-none', active ? 'text-accent' : 'text-text-muted')}
      />
      <span className="min-w-0 flex-1 truncate font-medium">{node.name}</span>
      {typeof page.memberCount === 'number' && page.memberCount > 0 && (
        <span className="inline-flex flex-none items-center gap-0.5 font-mono text-[10px] text-text-muted">
          <Users className="h-2.5 w-2.5" />
          {page.memberCount}
        </span>
      )}
    </button>
  );
}
